// Texts of the Claude support (settings page, the card after a shot and on its page, the next shot, the chat), in the
// app's style (WRITING.md 0, 22.10.2026; the boards "Claude …" of the prototype canvas). Merged into the app's text
// table (core/i18n.ts). Claude's own answers are model output and never part of this table.
import type { Lang } from '../core/i18n.js';

export const AI_TEXTS = {
  aiPrivacy: {
    de: 'Der Schlüssel liegt verschlüsselt im Konto. Für Vorschläge gehen die Bezugsdaten an Anthropic.',
    en: 'The key is stored encrypted in the account. For suggestions, shot data goes to Anthropic.',
  },
  aiKeyTitle: { de: 'Schlüssel', en: 'Key' },
  aiKeyInfo: {
    de: 'Der Schlüssel wird in der Anthropic Console angelegt. Vorschläge kosten Guthaben auf diesem Schlüssel.',
    en: 'The key is created in the Anthropic Console. Suggestions use credit on this key.',
  },
  aiKeyLabel: { de: 'API-Schlüssel von Anthropic', en: 'Anthropic API key' },
  aiKeyGet: { de: 'Schlüssel erstellen', en: 'Create a key' },
  aiKeySave: { de: 'Speichern', en: 'Save' },
  aiKeySaved: { de: 'Schlüssel gespeichert', en: 'Key saved' },
  aiKeySince: { de: 'endet auf {hint} · seit {date}', en: 'ends in {hint} · since {date}' },
  aiKeyReplace: { de: 'Ersetzen', en: 'Replace' },
  aiKeyRefused: {
    de: 'Anthropic hat den Schlüssel abgelehnt. Bitte prüfen und neu eingeben.',
    en: 'Anthropic rejected the key. Please check it and enter it again.',
  },
  aiForget: { de: 'Löschen', en: 'Delete' },
  aiForgetTitle: { de: 'Schlüssel löschen?', en: 'Delete the key?' },
  aiForgetText: {
    de: 'Ohne Schlüssel gibt es keine Vorschläge.',
    en: 'No suggestions without a key.',
  },
  // the key's end stays one piece: a word joiner after "…" ("…" / "wxYZ" broke over two lines at 360 px)
  aiLegacy: {
    de: 'Alter Schlüssel …\u2060{hint} in diesem Browser gefunden. Ins Konto übernehmen?',
    en: 'Old key …\u2060{hint} found in this browser. Move it to the account?',
  },
  aiLegacySave: { de: 'Übernehmen', en: 'Move to account' },
  aiLegacyDiscard: { de: 'Verwerfen', en: 'Discard' },
  aiModel: { de: 'Modell', en: 'Model' },
  aiModelInfo: { de: 'Sonnet ist günstiger.', en: 'Sonnet costs less.' },
  aiEffort: { de: 'Gründlichkeit', en: 'Thoroughness' },
  aiEffortInfo: {
    de: 'Gründlich dauert länger und kostet mehr.',
    en: 'Thorough takes longer and costs more.',
  },
  // asking after a rating (Dominik 22.10.2026: automatically on sour or bitter, can be switched off)
  aiAutoInfo: {
    de: 'Jede Anfrage kostet Guthaben auf dem Schlüssel.',
    en: 'Each request uses credit on the key.',
  },
  aiAuto: { de: 'Bei sauer oder bitter automatisch fragen', en: 'Ask automatically when sour or bitter' },
  aiEffortStandard: { de: 'Standard', en: 'Standard' },
  aiEffortThorough: { de: 'Gründlich', en: 'Thorough' },
  // the card says "Claude" already (board "Mühle"): the folded text's row just names what it is
  aiGrinderKnown: { de: 'Angaben zur Mühle', en: 'About the grinder' },
  aiGrinderSearching: { de: 'Claude sucht Angaben zur Mühle …', en: 'Claude is looking up the grinder …' },
  aiGrinderLookUp: { de: 'Mühle nachschlagen', en: 'Look up the grinder' },
  // board "Mühle": the card "Claude (i)" before the lookup
  aiGrinderTitle: { de: 'Claude', en: 'Claude' },
  aiGrinderInfo: {
    de: 'Claude sucht im Netz nach der Skala und üblichen Werten für Espresso. Das kostet Guthaben auf dem Schlüssel.',
    en: 'Claude searches the web for the scale and common espresso settings. This uses credit on the key.',
  },
  aiGrinderPitch: {
    de: 'Claude gibt Mahlgrad-Tipps passend zur {n}.',
    en: 'Claude gives grind tips to suit the {n}.',
  },
  aiGrinderAgain: { de: 'Erneut nachschlagen', en: 'Look up again' },
  aiSources: { de: 'Quellen', en: 'Sources' },
  // board "Eine Bohne" (25.10.2026, Dominik: "ja, zeilen und nur anzeigen"): the card "Claude (i)" under the bean's figures
  aiBeanInfo: {
    de: 'Claude sucht im Netz nach Röstung, Sorten und dem Rezept des Rösters. Das kostet Guthaben auf dem Schlüssel.',
    en: 'Claude searches the web for the roast, varieties and the roaster’s recipe. This uses credit on the key.',
  },
  aiBeanKnown: { de: 'Angaben zur Bohne', en: 'About the bean' },
  aiBeanSearching: { de: 'Claude sucht Angaben zur Bohne …', en: 'Claude is looking up the bean …' },
  aiBeanPitch: {
    de: 'Claude gibt Tipps passend zu dieser Bohne.',
    en: 'Claude gives tips to suit this bean.',
  },
  aiBeanLookUp: { de: 'Bohne nachschlagen', en: 'Look up the bean' },
  aiBeanMissed: {
    de: 'Claude hat die Bohne nicht sicher gefunden.',
    en: 'Claude couldn’t find this bean for sure.',
  },
  aiBeanRoast: { de: 'Röstung', en: 'Roast' },
  aiBeanVarieties: { de: 'Sorten', en: 'Varieties' },
  aiBeanRecipe: { de: 'Röster empfiehlt', en: 'Roaster suggests' },
  aiBeanNotes: { de: 'Geschmack', en: 'Tasting notes' },
  aiRoast1: { de: 'hell', en: 'light' },
  aiRoast2: { de: 'mittelhell', en: 'medium-light' },
  aiRoast3: { de: 'mittel', en: 'medium' },
  aiRoast4: { de: 'mitteldunkel', en: 'medium-dark' },
  aiRoast5: { de: 'dunkel', en: 'dark' },
  // the card (after a shot under the taste, on a shot's page at the top)
  aiClaude: { de: 'Claude', en: 'Claude' },
  aiSuggest: { de: 'Vorschlag von Claude', en: 'Suggestion from Claude' },
  aiMore: { de: 'Mehr', en: 'More' },
  aiReading0: { de: 'Claude liest diesen Bezug …', en: 'Claude is reading this shot …' },
  aiReading1: {
    de: 'Claude liest diesen und einen früheren Bezug …',
    en: 'Claude is reading this and one earlier shot …',
  },
  aiReadingN: {
    de: 'Claude liest diesen und {n} frühere Bezüge …',
    en: 'Claude is reading this and {n} earlier shots …',
  },
  aiLong: {
    de: 'Das dauert länger als üblich. Der Vorschlag erscheint später am Bezug.',
    en: 'This is taking longer than usual. The suggestion will appear on the shot later.',
  },
  aiHide: { de: 'Ausblenden', en: 'Hide' },
  aiSource0: { de: 'Aus diesem Bezug', en: 'From this shot' },
  aiSource1: { de: 'Aus diesem und einem früheren Bezug', en: 'From this and one earlier shot' },
  aiSourceN: { de: 'Aus diesem und {n} früheren Bezügen', en: 'From this and {n} earlier shots' },
  aiMistakesInfo: {
    de: 'Claude kann sich irren. Bitte Vorschlag vor dem Übernehmen prüfen.',
    en: 'Claude can make mistakes. Please check the suggestion before applying it.',
  },
  aiMistakes: { de: 'Claude kann sich irren.', en: 'Claude can make mistakes.' },
  aiAppliedNext: { de: 'Der nächste Bezug läuft mit {changes}.', en: 'The next shot uses {changes}.' },
  aiUndo: { de: 'Rückgängig', en: 'Undo' },
  // read out between the old and the new value (the arrow is only for the eye)
  aiTo: { de: 'auf', en: 'to' },
  aiSettings: { de: 'Einstellungen', en: 'Settings' },
  aiOpenSettings: { de: 'Einstellungen öffnen', en: 'Open settings' },
  // without a key: one quiet line after the first rated shot (Dominik 22.10.2026)
  aiSetup: { de: 'Claude-Vorschläge einrichten', en: 'Set up Claude suggestions' },
  aiSetupGo: { de: 'Einrichten', en: 'Set up' },
  aiSetupLater: { de: 'Nicht jetzt', en: 'Not now' },
  // the next shot (Bezug tab)
  aiInstead: { de: '{to} statt {from}', en: '{to} instead of {from}' },
  aiNextPending: { de: 'Claude schlägt {changes} vor', en: 'Claude suggests {changes}' },
  aiNextFromSour: { de: 'zum sauren Bezug von {when}', en: 'for the sour shot from {when}' },
  aiNextFromBitter: { de: 'zum bitteren Bezug von {when}', en: 'for the bitter shot from {when}' },
  aiNextFrom: { de: 'zum Bezug von {when}', en: 'for the shot from {when}' },
  aiNextApplied: {
    de: '{changes} von Claude, zum Bezug von {when}',
    en: '{changes} from Claude, for the shot from {when}',
  },
  aiThinking: { de: 'Claude denkt nach …', en: 'Claude is thinking …' },
  aiForNext: { de: 'Für den nächsten Bezug', en: 'For the next shot' },
  aiApply: { de: 'Übernehmen', en: 'Apply' },
  aiApplied: { de: 'Übernommen', en: 'Applied' },
  aiApplyOtherBean: {
    de: '„{bean}“ ist gewählt. Bitte zuerst die Bohne dieses Bezugs wählen.',
    en: '“{bean}” is selected. Please choose this shot’s bean first.',
  },
  aiApplyOffline: {
    de: 'Maschine ist gerade nicht erreichbar.',
    en: 'Machine can’t be reached right now.',
  },
  aiApplyRights: {
    de: 'Keine Berechtigung, diese Werte zu ändern.',
    en: 'No permission to change these values.',
  },
  aiAskMore: { de: 'Weiter fragen', en: 'Ask more' },
  aiAgain: { de: 'Neu fragen', en: 'Ask again' },
  aiRetry: { de: 'Erneut versuchen', en: 'Try again' },
  aiFiner: { de: 'feiner', en: 'finer' },
  aiCoarser: { de: 'gröber', en: 'coarser' },
  aiChatPh: { de: 'Nachricht an Claude', en: 'Message Claude' },
  aiChatSend: { de: 'Senden', en: 'Send' },
  aiChatClose: { de: 'Schließen', en: 'Close' },
  aiChatShot: { de: 'Bezug von {when}', en: 'Shot from {when}' },
  aiChipWhy: { de: 'Warum diese Änderung?', en: 'Why this change?' },
  aiChipSmaller: { de: 'Lieber ein kleinerer Schritt', en: 'Rather a smaller step' },
  aiChipSour: { de: 'Er war eher sauer', en: 'It was rather sour' },
  aiChipBitter: { de: 'Er war eher bitter', en: 'It was rather bitter' },
  aiChatEmpty: {
    de: 'Zum Beispiel: Warum war der Bezug sauer?',
    en: 'For example: why was the shot sour?',
  },
  aiYou: { de: 'Du', en: 'You' },
  aiErr_offline: {
    de: 'Keine Verbindung zur Cloud. Bitte gleich noch einmal versuchen.',
    en: 'No connection to the cloud. Please try again in a moment.',
  },
  aiErr_noKey: {
    de: 'Kein Claude-Schlüssel gespeichert. Bitte unter Einstellungen → Claude eintragen.',
    en: 'No Claude key saved. Please add one in Settings → Claude.',
  },
  aiErr_key: {
    de: 'Anthropic hat den Schlüssel abgelehnt. Bitte unter Einstellungen → Claude einen neuen eintragen.',
    en: 'Anthropic rejected the key. Please add a new one in Settings → Claude.',
  },
  aiErr_format: {
    de: 'Ein API-Schlüssel von Anthropic beginnt mit „sk-ant-“.',
    en: 'An Anthropic API key starts with “sk-ant-”.',
  },
  aiErr_credit: {
    de: 'API-Guthaben ist aufgebraucht. Bitte in der Anthropic-Konsole aufladen.',
    en: 'API credit has run out. Please top it up in the Anthropic Console.',
  },
  aiErr_model: {
    de: 'Dieses Modell ist mit diesem Schlüssel nicht verfügbar. Bitte unter Einstellungen → Claude ein anderes wählen.',
    en: 'This model isn’t available with this key. Please choose another in Settings → Claude.',
  },
  aiErr_rateLimited: {
    de: 'Der Schlüssel hat sein Anfragelimit erreicht. Bitte später noch einmal versuchen.',
    en: 'The key has hit its request limit. Please try again later.',
  },
  aiErr_limit: {
    de: 'Zu viele Anfragen. Bitte gleich noch einmal versuchen.',
    en: 'Too many requests. Please try again in a moment.',
  },
  aiErr_refusal: {
    de: 'Claude hat diese Anfrage abgelehnt.',
    en: 'Claude declined this request.',
  },
  aiErr_empty: {
    de: 'Keine brauchbare Antwort von Claude. Bitte noch einmal fragen.',
    en: 'No usable answer from Claude. Please ask again.',
  },
  aiErr_unavailable: {
    de: 'Claude ist gerade nicht erreichbar. Bitte gleich noch einmal versuchen.',
    en: 'Claude can’t be reached right now. Please try again in a moment.',
  },
} as const satisfies Record<string, Record<Lang, string>>;
